import {
    PublicClientApplication,
    InteractionRequiredAuthError,
    AccountInfo,
    AuthenticationResult,
} from '@azure/msal-browser';
import logger from '@/shared/lib/logger';

// Variável de ambiente para desabilitar autenticação Azure
const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

// Configuração Azure a partir de variáveis de ambiente
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || '';
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID || '';
const authorityEnv = (import.meta.env.VITE_AZURE_AUTHORITY || '').replace(/\/+$/, '');
const authority = authorityEnv || (tenantId ? `https://login.microsoftonline.com/${tenantId}` : '');
const redirectUri = import.meta.env.VITE_AZURE_REDIRECT_URI || `${window.location.origin}/callback`;
const postLogoutRedirectUri = 
    import.meta.env.VITE_AZURE_POST_LOGOUT_REDIRECT_URI || window.location.origin;

// Valores padrão para desenvolvimento
const resolvedClientId = clientId || '00000000-0000-0000-0000-000000000000';
const resolvedAuthority = authority || 'https://login.microsoftonline.com/common';

/** Escopos OIDC; o JWT enviado ao backend é o id_token (aud = client id da app). */
const getScopes = (): string[] => {
    const scopesEnv = import.meta.env.VITE_AZURE_SCOPES;
    if (scopesEnv) {
        return scopesEnv.split(',').map((s: string) => s.trim());
    }

    return ['openid', 'profile', 'email'];
};

const idTokenFromResult = (result: AuthenticationResult): string | null => {
    const id = result.idToken?.trim();
    return id || null;
};

// Configuração MSAL
const msalConfig = {
    auth: {
        clientId: resolvedClientId,
        authority: resolvedAuthority,
        redirectUri,
        postLogoutRedirectUri,
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: 'sessionStorage' as const,
        storeAuthStateInCookie: false,
    },
};

// Instância MSAL
const msalInstance = new PublicClientApplication(msalConfig);

// Rastrear estado de inicialização
let initializationPromise: Promise<boolean> | null = null;
let isInitialized = false;

// Cache do id_token enviado ao backend (Bearer)
let cachedIdToken: string | null = null;

/**
 * Obtém a conta ativa do MSAL
 */
const getAccount = (): AccountInfo | null => {
    if (DISABLE_AUTH) {
        return null;
    }
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
};

/**
 * Inicializa Azure Entra ID (MSAL)
 * @returns Promise<boolean> - Se o usuário está autenticado
 */
export const initEntra = (): Promise<boolean> => {
    logger.info('[Azure SSO] Starting initialization...', {
        clientId: resolvedClientId ? `${resolvedClientId.substring(0, 8)}...` : 'NOT SET',
        authority: resolvedAuthority,
        redirectUri,
    });

    if (DISABLE_AUTH) {
        logger.warn('[Azure SSO] Auth disabled via VITE_DISABLE_AUTH');
        return Promise.resolve(true);
    }

    if (isInitialized) {
        const account = getAccount();
        const authenticated = !!account;
        logger.debug('[Azure SSO] Already initialized', { 
            authenticated,
            accountEmail: account?.username || 'none'
        });
        return Promise.resolve(authenticated);
    }

    if (initializationPromise) {
        logger.debug('[Azure SSO] Initialization already in progress');
        return initializationPromise;
    }

    logger.info('[Azure SSO] Initializing MSAL instance...');
    initializationPromise = msalInstance
        .initialize()
        .then(() => {
            logger.info('[Azure SSO] MSAL instance initialized, handling redirect promise...');
            return msalInstance.handleRedirectPromise();
        })
        .then(async (result) => {
            if (result?.account) {
                logger.info('[Azure SSO] Account found in redirect result', {
                    username: result.account.username,
                    name: result.account.name,
                    localAccountId: result.account.localAccountId,
                });
                msalInstance.setActiveAccount(result.account);
            }
            const redirectIdToken = result ? idTokenFromResult(result) : null;
            if (redirectIdToken) {
                logger.info('[Azure SSO] ID token received from redirect', {
                    tokenLength: redirectIdToken.length,
                    expiresOn: result?.expiresOn?.toISOString(),
                });
                cachedIdToken = redirectIdToken;
            }
            const account = getAccount();
            if (account) {
                logger.info('[Azure SSO] Active account set', {
                    username: account.username,
                    name: account.name,
                });
                msalInstance.setActiveAccount(account);
                
                if (!cachedIdToken) {
                    logger.info('[Azure SSO] No id token from redirect, acquiring silently...');
                    const newToken = await updateToken(60);
                    if (newToken) {
                        logger.info('[Azure SSO] Token acquired during initialization', {
                            tokenLength: newToken.length,
                        });
                    }
                }
            } else {
                logger.info('[Azure SSO] No active account found - user not authenticated');
            }
            isInitialized = true;
            const authenticated = !!account;
            logger.info('[Azure SSO] Initialization complete', { 
                authenticated,
                hasToken: !!cachedIdToken,
                tokenLength: cachedIdToken?.length || 0,
            });
            return authenticated;
        })
        .catch((error) => {
            logger.error('[Azure SSO] Initialization failed', {
                error: error.message,
                errorCode: error.errorCode,
                errorMessage: error.errorMessage,
                stack: error.stack,
            });
            initializationPromise = null;
            return false;
        });

    return initializationPromise;
};

/**
 * Renova o id_token (acquireTokenSilent) para manter sessão com o backend.
 */
export const updateToken = async (minValidity: number = 30): Promise<string | null> => {
    if (DISABLE_AUTH) {
        const dummyToken = 'dummy-token';
        cachedIdToken = dummyToken;
        return dummyToken;
    }

    const account = getAccount();
    if (!account) {
        logger.debug('[Azure SSO] updateToken: No account found');
        return null;
    }

    logger.debug('[Azure SSO] Updating token', {
        minValidity,
        accountUsername: account.username,
    });

    msalInstance.setActiveAccount(account);

    try {
        const result = await msalInstance.acquireTokenSilent({
            account,
            scopes: getScopes(),
        });

        const idToken = idTokenFromResult(result);
        if (!idToken) {
            logger.error('[Azure SSO] acquireTokenSilent returned no id token');
            return null;
        }

        logger.info('[Azure SSO] ID token acquired silently', {
            tokenLength: idToken.length,
            expiresOn: result.expiresOn?.toISOString(),
            scopes: result.scopes,
        });

        cachedIdToken = idToken;
        return idToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            logger.warn('[Azure SSO] Token refresh requires user interaction', {
                errorCode: error.errorCode,
                errorMessage: error.errorMessage,
            });
            return null;
        }
        logger.error('[Azure SSO] Failed to refresh token', {
            error: error instanceof Error ? error.message : String(error),
            errorCode: (error as any)?.errorCode,
        });
        return null;
    }
};

/**
 * Inicia o fluxo de login.
 */
export const loginEntra = (): void => {
    if (DISABLE_AUTH) {
        logger.debug('[Azure SSO] Auth disabled, simulating login');
        window.location.href = window.location.origin;
        return;
    }

    logger.info('[Azure SSO] Initiating login redirect', {
        redirectUri: window.location.origin + '/callback',
        scopes: getScopes(),
    });

    msalInstance.loginRedirect({
        scopes: getScopes(),
        redirectStartPage: window.location.origin + '/callback',
    }).catch((error) => {
        logger.error('[Azure SSO] Login redirect failed', {
            error: error.message,
            errorCode: error.errorCode,
            errorMessage: error.errorMessage,
        });
    });
};

/**
 * Inicia o fluxo de logout.
 */
export const logoutEntra = (): void => {
    if (DISABLE_AUTH) {
        logger.debug('Azure disabled, simulating logout');
        window.location.href = window.location.origin;
        return;
    }

    const account = getAccount();
    if (account) {
        msalInstance.logoutRedirect({
            account,
            postLogoutRedirectUri,
        }).catch((error) => {
            logger.error('Logout redirect failed:', error);
        });
    } else {
        // Sem conta, apenas redirecionar
        window.location.href = window.location.origin;
    }
};

/** Retorna o id_token atual para o header Authorization. */
export const getToken = (): string | undefined => {
    if (DISABLE_AUTH) {
        return 'dummy-token';
    }
    const token = cachedIdToken || undefined;
    if (token) {
        logger.debug('[Azure SSO] Token retrieved', {
            tokenLength: token.length,
            tokenPreview: `${token.substring(0, 20)}...`,
        });
    } else {
        logger.debug('[Azure SSO] No token available');
    }
    return token;
};

/**
 * Verifica se o usuário está autenticado.
 */
export const isAuthenticated = (): boolean => {
    if (DISABLE_AUTH) {
        return true;
    }
    const account = getAccount();
    const authenticated = !!account;
    logger.debug('[Azure SSO] Authentication check', {
        authenticated,
        accountEmail: account?.username || 'none',
    });
    return authenticated;
};

/**
 * Obtém informações do usuário do token/conta.
 */
export const getUserInfo = () => {
    if (DISABLE_AUTH) {
        return {
            sub: 'dummy_sub',
            id: 'dummy_id',
            name: 'Usuário Dummy',
            email: 'dummy@example.com',
            preferred_username: 'dummyuser',
            given_name: 'Usuário',
            family_name: 'Dummy',
        };
    }

    const account = getAccount();
    if (!account) {
        logger.debug('[Azure SSO] getUserInfo: No account found');
        return null;
    }

    // Mapear conta Azure para formato comum de informações do usuário
    const userInfo = {
        sub: account.idTokenClaims?.sub || account.homeAccountId,
        id: account.localAccountId || account.homeAccountId,
        name: account.name || account.idTokenClaims?.name,
        email: account.username || account.idTokenClaims?.email,
        preferred_username: account.username,
        given_name: account.idTokenClaims?.given_name,
        family_name: account.idTokenClaims?.family_name,
    };

    logger.debug('[Azure SSO] User info retrieved', {
        email: userInfo.email,
        name: userInfo.name,
        hasSub: !!userInfo.sub,
    });

    return userInfo;
};

export default msalInstance;
